
import React, { useState } from 'react';
import { DataTable } from '@/components/DataTable';
import { ConnectionErrorAlert } from './bikes/ConnectionErrorAlert';
import { BikeActions } from './bikes/BikeActions';
import { getBikeColumns } from './bikes/BikeTableColumns';
import { BikeDialogs } from './bikes/components/BikeDialogs';
import { useBikesData } from './bikes/hooks/useBikesData';
import { BikeRentalsDialog } from './bikes/components/BikeRentalsDialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { MovementControlPanel } from '@/components/bikes/MovementControlPanel';
import { toggleBikeMovementBlock } from './bikes/services/movementControlApi';
import { useBulkBikeActions } from '@/components/bikes/BulkBikeOperations';
import { bulkBlockBikes, bulkUnblockBikes, bulkSetSpeedLimit, bulkResetRestrictions } from '@/services/bulkMovementControlApi';
import { Bike } from '@/services/types';
import { toast } from '@/components/ui/sonner';
import { BikeStatsCard } from './bikes/components/BikeStatsCard';

const BikesPage: React.FC = () => {
  const {
    bikes,
    allBikes,
    loading,
    statsLoading,
    selectedBike,
    showAddDialog,
    showEditDialog,
    connectionError,
    dataSource,
    totalCount,
    setSelectedBike,
    setShowAddDialog,
    setShowEditDialog,
    setStatusFilter,
    setMovementFilter,
    setSearchQuery,
    handleRefreshData,
    handleAddBike,
    handleEditBike,
    handleDeleteBike
  } = useBikesData();

  // Состояние для диалога связанных аренд
  const [showRentalsDialog, setShowRentalsDialog] = useState(false);
  const [showMovementControlDialog, setShowMovementControlDialog] = useState(false);
  const [movementControlBike, setMovementControlBike] = useState<Bike | null>(null);

  const handleViewRentals = (bike: any) => {
    setSelectedBike(bike);
    setShowRentalsDialog(true);
  };

  const handleToggleMovementBlock = async (bike: Bike) => {
    const newBlocked = !bike.movement_blocked;
    const result = await toggleBikeMovementBlock(bike.id.toString(), newBlocked);
    
    if (result.success) {
      toast.success(
        newBlocked 
          ? 'Велосипед успешно заблокирован' 
          : 'Велосипед успешно разблокирован'
      );
      await handleRefreshData();
    } else {
      toast.error('Ошибка при изменении состояния блокировки');
    }
  };

  const handleOpenSpeedLimitDialog = (bike: Bike) => {
    setMovementControlBike(bike);
    setShowMovementControlDialog(true);
  };

  const handleMovementControlUpdate = async () => {
    await handleRefreshData();
    setShowMovementControlDialog(false);
    setMovementControlBike(null);
  };

  // Обработчики массовых операций с улучшенными уведомлениями
  const handleBulkBlock = async (bikes: Bike[]) => {
    const bikesToBlock = bikes.filter(bike => !bike.movement_blocked);
    if (bikesToBlock.length === 0) {
      toast.error('Все выбранные велосипеды уже заблокированы');
      return;
    }

    const bikeIds = bikesToBlock.map(bike => bike.id.toString());
    const result = await bulkBlockBikes(bikeIds);
    
    if (result.success && result.updatedCount > 0) {
      toast.success(`Успешно заблокировано ${result.updatedCount} велосипедов`);
      await handleRefreshData();
    }
  };

  const handleBulkUnblock = async (bikes: Bike[]) => {
    const bikesToUnblock = bikes.filter(bike => bike.movement_blocked);
    if (bikesToUnblock.length === 0) {
      toast.error('Все выбранные велосипеды уже разблокированы');
      return;
    }

    const bikeIds = bikesToUnblock.map(bike => bike.id.toString());
    const result = await bulkUnblockBikes(bikeIds);
    
    if (result.success && result.updatedCount > 0) {
      toast.success(`Успешно разблокировано ${result.updatedCount} велосипедов`);
      await handleRefreshData();
    }
  };

  const handleBulkSetSpeedLimit = async (bikes: Bike[], speedLimit: number) => {
    const bikeIds = bikes.map(bike => bike.id.toString());
    const result = await bulkSetSpeedLimit(bikeIds, speedLimit);
    
    if (result.success && result.updatedCount > 0) {
      toast.success(`Установлено ограничение скорости ${speedLimit} км/ч для ${result.updatedCount} велосипедов`);
      await handleRefreshData();
    }
  };

  const handleBulkResetRestrictions = async (bikes: Bike[]) => {
    const bikesWithRestrictions = bikes.filter(bike => bike.movement_blocked || bike.speed_limit);
    if (bikesWithRestrictions.length === 0) {
      toast.error('У выбранных велосипедов нет активных ограничений');
      return;
    }

    const bikeIds = bikesWithRestrictions.map(bike => bike.id.toString());
    const result = await bulkResetRestrictions(bikeIds);
    
    if (result.success && result.updatedCount > 0) {
      toast.success(`Сброшены ограничения для ${result.updatedCount} велосипедов`);
      await handleRefreshData();
    }
  };

  // Получаем массовые действия и диалоги
  const { actions: bulkActions, dialogs } = useBulkBikeActions({
    onBulkBlock: handleBulkBlock,
    onBulkUnblock: handleBulkUnblock,
    onBulkSetSpeedLimit: handleBulkSetSpeedLimit,
    onBulkResetRestrictions: handleBulkResetRestrictions
  });

  const columns = getBikeColumns({
    onEditClick: (bike) => {
      setSelectedBike(bike);
      setShowEditDialog(true);
    },
    onDeleteClick: handleDeleteBike,
    onViewRentalsClick: handleViewRentals,
    onToggleMovementBlock: handleToggleMovementBlock,
    onOpenSpeedLimitDialog: handleOpenSpeedLimitDialog
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Управление велосипедами</h1>
        <BikeActions 
          fetchBikes={handleRefreshData}
          dataSource={dataSource}
          setStatusFilter={setStatusFilter}
          setMovementFilter={setMovementFilter}
          bikes={allBikes}
          setShowAddDialog={setShowAddDialog}
        />
      </div>

      <ConnectionErrorAlert 
        connectionError={connectionError} 
        dataSource={dataSource} 
      />

      <BikeStatsCard 
        bikes={allBikes} 
        totalCount={totalCount}
        loading={statsLoading}
      />

      <DataTable
        data={bikes}
        columns={columns}
        searchable={true}
        searchPlaceholder="Поиск по серийному номеру, GPS-тегу, модели или курьеру..."
        loading={loading}
        selectable={true}
        bulkActions={bulkActions}
        onRowClick={(bike) => {
          setSelectedBike(bike);
          setShowEditDialog(true);
        }}
        onSearch={setSearchQuery}
      />

      <BikeDialogs
        selectedBike={selectedBike}
        showAddDialog={showAddDialog}
        showEditDialog={showEditDialog}
        setShowAddDialog={setShowAddDialog}
        setShowEditDialog={setShowEditDialog}
        handleAddBike={handleAddBike}
        handleEditBike={handleEditBike}
        loading={loading}
      />

      {/* Диалог для отображения связанных аренд */}
      <BikeRentalsDialog
        bike={selectedBike}
        open={showRentalsDialog}
        onOpenChange={setShowRentalsDialog}
      />

      {/* Диалог управления движением */}
      <Dialog open={showMovementControlDialog} onOpenChange={setShowMovementControlDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              Управление движением - {movementControlBike?.model} ({movementControlBike?.serialNumber})
            </DialogTitle>
          </DialogHeader>
          {movementControlBike && (
            <MovementControlPanel
              bikeId={movementControlBike.id.toString()}
              currentBlocked={movementControlBike.movement_blocked}
              currentSpeedLimit={movementControlBike.speed_limit}
              maxAllowedSpeed={movementControlBike.max_allowed_speed}
              lastMovementUpdate={movementControlBike.last_movement_update}
              onUpdate={handleMovementControlUpdate}
              loading={loading}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Диалоги подтверждения массовых операций */}
      {dialogs}
    </div>
  );
};

export default BikesPage;
